/* eslint-disable @typescript-eslint/no-explicit-any */
import { initializeFaro, ErrorsInstrumentation, CSPInstrumentation } from '@grafana/faro-web-sdk';
import { faroIgnoreErrors } from './faroIgnoreErrors';

interface HandleSendErrorArgsType {
  errorMessage?: string | Event;
  error?: Error;
  data?: any;
}

const removeNumberFromString = (string: string) => string.replace(/\d/g, '');

export const getValue = (value: string) => value.replace(/(?:https?|ftp):\/\/[\n\S]+/g, '');

const botUserAgentPattern =
  /(?:googlebot|bingbot|yandexbot|duckduckbot|baiduspider|applebot|twitterbot|linkedinbot|facebookexternalhit|meta-webindexer|discordbot|telegrambot|slackbot|whatsapp|embedly|ia_archiver|ahrefsbot|semrushbot|mj12bot|dotbot|petalbot|bytespider|pingdom|uptimerobot|crawler|spider|slurp)/i;

export const isBotUserAgent = () => botUserAgentPattern.test(navigator.userAgent || '');

export const getPageUrl = (string: string) => {
  const { pathname } = new URL(string);

  return removeNumberFromString(pathname);
};

export const shouldDropMessage = (msg: string) => {
  if (!msg) return false;

  const patterns = [
    /Object Not Found Matching Id/i,
    /MethodName:\s*update/i,
    /chrome-extension:\/\//i,
    /Can't\s+find\s+variable:\s*chrome/i,
    /chrome\s+is\s+not\s+defined/i,
    /closePlugin@/i,
  ];

  return patterns.some((re) => re.test(msg));
};

const safeToString = (val: unknown): string => {
  if (val == null) return '';
  if (val instanceof Error) return val.stack || val.message || String(val);
  if (typeof val === 'string') return val;
  if (typeof Event !== 'undefined' && val instanceof Event) {
    const ev = val as Event & { isTrusted?: boolean };

    return `Event(${ev.type}) isTrusted=${Boolean(ev.isTrusted)}`;
  }
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const setupFaroErrorReporting = () => {
  if (process.env.SENDIBLE_ENV === 'production' && !isBotUserAgent()) {
    const faroUrl = `https://grafana-alloy.prod.sendible.com/collect`;
    const faroAppName = `frontend-faro`;

    const faro = initializeFaro({
      url: faroUrl,
      apiKey: 'glsa_qP6At1xfSP5mZwXBVjRRWE6KAVP01E01_e8313f77',
      app: {
        name: faroAppName,
        version: '1.0.0',
      },
      instrumentations: [new ErrorsInstrumentation(), new CSPInstrumentation()],
      trackResources: false,
      consoleInstrumentation: {
        consoleErrorAsLog: true,
      },
      dedupe: true,
      beforeSend: (item: any) => {
        const extraSessionData = {
          dashboard_version: String(window.dashboard_version) || '0',
          isTrialistUser: String(window.$current_user?.user_status?.toLowerCase().includes('on trial')) || 'undefined',
          original_url: item.meta.page.url,
          original_value: item.payload.value,
        };

        if (!item.meta.session) {
          item.meta.session = {
            attributes: {
              ...extraSessionData,
            },
          };
        } else {
          item.meta.session = {
            ...item.meta.session,
            attributes: {
              ...item.meta.session.attributes,
              ...extraSessionData,
            },
          };
        }

        item.meta.page.url = getPageUrl(item.meta.page.url || '');
        item.payload.value = getValue(item.payload.value || '');

        if (shouldDropMessage(item.payload.value)) {
          return null;
        }

        return item;
      },
    });

    const handleSendErrorToFaro = ({ error, errorMessage, data }: HandleSendErrorArgsType) => {
      const parts: string[] = [];

      if (typeof errorMessage !== 'undefined') parts.push(safeToString(errorMessage));
      if (error?.stack) parts.push(error.stack);

      const stringifiedErrors = Array.from(new Set(parts)).filter(Boolean);
      const errorString = stringifiedErrors.join(' ');

      if (faroIgnoreErrors.map((e: string) => errorString.includes(e)).some((v: boolean) => v)) return;

      if (!errorString || shouldDropMessage(errorString)) return;

      const config = data ? { context: { data } } : {};

      faro.api.pushError(new Error(errorString), config);
    };

    window.addEventListener(
      'reportErrorToFaro',
      function ({ detail }: any) {
        if (!detail) return;

        const data: HandleSendErrorArgsType = {
          error: detail.error,
          errorMessage: detail.error?.message || undefined,
          data: detail.data || undefined,
        };

        handleSendErrorToFaro(data);
      },
      { capture: true, once: false, passive: false }
    );

    window.onerror = function (errorMessage, fileName, lineNumber, column, error) {
      if (typeof error === 'undefined') {
        return true;
      }

      handleSendErrorToFaro({ errorMessage, error });
    };

    window.onunhandledrejection = function (error) {
      const reason = (error as any)?.reason;

      if (typeof reason === 'undefined') return;
      if (reason instanceof Error) {
        handleSendErrorToFaro({ errorMessage: reason.message, error: reason });

        return;
      }
      handleSendErrorToFaro({ errorMessage: safeToString(reason), error: undefined });
    };
  }
};

export default setupFaroErrorReporting;
